import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { affordableLabel } from '../util/balance-labels';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { TelegramContactService } from '../../../ui/telegram-contact';
import { AccountFacade } from '../domain/application/account.facade';

function formatRub(n: number): string {
  return new Intl.NumberFormat('ru-RU').format(n);
}

@Component({
  selector: 'app-balance-dialog',
  standalone: true,
  imports: [HlmButton, DialogSheetComponent, NgIcon],
  viewProviders: [provideIcons({ lucideX })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <button
          type="button"
          class="absolute right-3.5 top-3.5 grid size-7.5 place-items-center rounded-full border border-line2
                 text-sm text-c3 transition-colors hover:bg-panel2 hover:text-ink"
          aria-label="Закрыть"
          (click)="close()"
        >
          <ng-icon name="lucideX" />
        </button>

        <div class="mb-4 mt-1 flex justify-center">
          <div class="flex h-21 w-21 items-center justify-center rounded-full bg-acc-softer text-acc2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M16 12h2" />
            </svg>
          </div>
        </div>

        <p class="text-4xl font-semibold leading-[1.1] tracking-[-0.02em] text-ink">{{ amountFormatted() }} ₽</p>
        <p class="mt-1.5 text-sm text-c2">Ваш баланс</p>
        <!-- Row always reserved: a tariff retry landing while open fills it in place, no layout shift. -->
        <p data-testid="balance-hint" class="mt-4.5 min-h-6 text-base font-semibold text-acc2">{{ hint() }}</p>

        <div class="my-5.5 rounded-[var(--r-dialog-inner)] border border-line pt-5.5 px-5.5 pb-4.5 text-center">
          <p class="text-sm text-c2">Стоимость одного класса</p>
          <p class="mt-1 border-b border-line pb-3.5 text-lg font-semibold text-ink">{{ priceLabel() }}</p>
          <p class="mt-3.5 text-sm text-c2">Размер класса не влияет на стоимость.</p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <button hlmBtn type="button" variant="outline" class="min-h-12.5 justify-center" (click)="openTopUp()">
            Как пополнить
          </button>
          <button hlmBtn type="button" class="min-h-12.5 justify-center" (click)="close()">Понятно</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class BalanceDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('s', 'p-9') + ' relative text-center';
  // Live, not a snapshot: the opener retries a failed tariff leg, and its answer must land in this open window.
  private readonly facade = inject(AccountFacade);
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly telegramContact = inject(TelegramContactService);

  protected readonly amountFormatted = computed(() => formatRub(this.facade.balanceAmount()));
  // Tariff unread (billing down) = unknown price, never a fake "0 ₽".
  protected readonly priceLabel = computed(() => {
    const price = this.facade.albumPrice();
    return price === null ? '—' : `${formatRub(price)} ₽ за класс`;
  });

  protected readonly hint = computed(() => affordableLabel(this.facade.affordableAlbumsCount()));

  protected openTopUp(): void {
    this.dialogRef.close();
    this.telegramContact.open('topup');
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
